"use client";

import { Graphics } from "@/components/graphics";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import {
  EVENT_HANDOVER_MESSAGE,
  FULL_PAYMENT_CONFIRMATION_MESSAGE,
  PICKUP_HOURS_MESSAGE,
} from "@/lib/quote-next-steps";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

const getCards = (locale: Locales) => {
  return [
    {
      icon: <Graphics.magnifyingGlassCircle />,
      title: __("Processing your request", locale),
      description: __("We are reviewing your request.", locale),
    },
    {
      icon: <Graphics.document />,
      title: __("Submitting a quote", locale),
      description: __("We are sending you a revised final quote.", locale),
    },
    {
      icon: <Graphics.okHand />,
      title: __("Written confirmation from you", locale),
      description: __(FULL_PAYMENT_CONFIRMATION_MESSAGE, locale),
    },
    {
      icon: <Graphics.shipmentBox />,
      title: __("Preparing your order", locale),
      description: __("We are carefully preparing your equipment.", locale),
    },
    {
      icon: <Graphics.partyPopper />,
      title: __("Event", locale),
      description: __(EVENT_HANDOVER_MESSAGE, locale),
    },
  ];
};

type ThankYouSidebarProps = {
  locale: Locales;
};

const ThankYouSidebar = ({ locale }: ThankYouSidebarProps) => {
  const cards = getCards(locale);
  const searchParams = useSearchParams();
  const workflowError = searchParams.get("error");

  if (workflowError) return null;

  return (
    <aside className="space-y-5.5 md:max-w-[304px] md:flex-1/3">
      <h5 className="mb-6.5 text-lg min-md:hidden">{__("Next steps :", locale)}</h5>
      <ScrollArea className="max-w-screen">
        <ul className="border-primary-light/50 flex w-full gap-[7px] md:flex-col md:border-b">
          {cards.map((card, index) => (
            <li
              key={index}
              className={cn(
                "leading-body-small w-[245px] space-y-2.5 rounded-md px-4.5 py-4 text-sm",
                index === 0 && "bg-primary-light/5",
                index === 4 && "mr-6"
              )}
            >
              {card.icon}
              <h4 className={cn(index === 0 && "font-semibold")}>{card.title}</h4>
              <p className="text-primary-light">{card.description}</p>
            </li>
          ))}
        </ul>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <div className="leading-body-small space-y-2.5 text-sm max-md:hidden">
        <p className="">
          {__(
            "You can still adjust your order up to 72 business hours before the event by sending us an email including your order number.",
            locale
          )}
        </p>
        <hr className="border-primary-light/50" />
        <p>{__(PICKUP_HOURS_MESSAGE, locale)}</p>
        <Link href={getPermalink(locale, "faq")} className="text-pink-primary underline underline-offset-1">
          F.A.Q
        </Link>
      </div>
    </aside>
  );
};

export default ThankYouSidebar;
